import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  Alert,
  Tooltip,
  Stack,
  Collapse,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Checkbox,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoOutlinedIcon,
  CompareArrows as CompareArrowsIcon,
  AccountCircle as AccountCircleIcon,
  ExpandLess as ExpandLessIcon,
  CheckBox as CheckBoxIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import YearAccessEditor from "./YearAccessEditor";
import { RoleChip, RoleButtonGroup } from "./RoleSelector";
import { RestrictedChip } from "../../CompanyList";

const UserMode = ({
  selectedUserId,
  allUsers,
  handleImportPermissions,
  handleUpdatePermission,
  handleSaveAllPermissions,
  handleSavePermission,
  handleSelectAllYears,
  handleToggleYear,
  getAvailableYears,
  userPermissions,
  loadingPermission,
  savingPermission,
  setEditingPermission,
  importingPermission,
  importFromUserId,
  setImportFromUserId,
  editingPermission,
  filteredUsers,
  selectedUserInactive = false,
}) => {
  const [copyPermissionsExpanded, setCopyPermissionsExpanded] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [bulkRole, setBulkRole] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [includeRestrictedCompanies, setIncludeRestrictedCompanies] =
    useState(false);

  const handleStartEdit = (index) => {
    if (selectedUserInactive) return;
    const perm = userPermissions[index];
    if (perm) {
      setEditSnapshot(JSON.parse(JSON.stringify(perm)));
    }
    setEditingPermission(index);
  };

  const handleCancelEdit = () => {
    if (editSnapshot != null && editingPermission >= 0) {
      handleUpdatePermission(editingPermission, "role", editSnapshot.role);
      handleUpdatePermission(
        editingPermission,
        "is_partial_access",
        editSnapshot.is_partial_access
      );
      handleUpdatePermission(
        editingPermission,
        "allowed_yoa",
        editSnapshot.allowed_yoa ?? []
      );
    }
    setEditingPermission(-1);
    setEditSnapshot(null);
  };

  const handleToggleSelectPermission = (index) => {
    if (selectedUserInactive) return;
    setSelectedPermissions((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index];
    });
  };

  const handleSelectAllPermissions = (checked) => {
    if (selectedUserInactive) return;
    if (checked) {
      setSelectedPermissions(userPermissions.map((_, index) => index));
    } else {
      setSelectedPermissions([]);
    }
  };

  const handleBulkAssignRole = async () => {
    if (selectedUserInactive) return;
    if (!bulkRole || selectedPermissions.length === 0) return;

    setBulkAssigning(true);
    try {
      const updated = userPermissions.map((permission, index) => {
        const isSelected = selectedPermissions.includes(index);
        const shouldSkipRestricted =
          !includeRestrictedCompanies && permission?.isRestricted;

        if (!isSelected || shouldSkipRestricted) return permission;

        return {
          ...permission,
          role: bulkRole,
        };
      });

      await handleSaveAllPermissions(updated);
      setSelectedPermissions([]);
      setBulkRole("");
      setIncludeRestrictedCompanies(false);
    } catch (error) {
      console.error("Error during bulk role assignment:", error);
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleCancelBulkSelection = () => {
    setSelectedPermissions([]);
    setBulkRole("");
    setIncludeRestrictedCompanies(false);
  };

  const isAllSelected =
    userPermissions.length > 0 &&
    selectedPermissions.length === userPermissions.length;
  const isSomeSelected = selectedPermissions.length > 0 && !isAllSelected;
  const selectedUser = allUsers.find((u) => u?.u_id === selectedUserId);

  return (
    <>
      {!selectedUserId ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <TuneIcon sx={{ fontSize: 32, color: "grey.400" }} />
          </Box>
          <Typography variant='h6' color='text.primary' gutterBottom>
            Select a member to manage permissions
          </Typography>
          <Typography variant='body2'>
            Choose a user from the left panel to view or edit their access
            rights.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Toolbar: 用户信息与操作 */}
          <Box
            sx={{
              px: 4,
              py: 2.5,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "primary.lighter",
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: "1.2rem",
                }}
              >
                {(selectedUser?.name || "").charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant='h6' fontWeight={600} lineHeight={1.2}>
                  {selectedUser?.name}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {selectedUser?.email}
                </Typography>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor:
                  selectedPermissions.length > 0
                    ? "primary.lighter"
                    : "action.hover",
                borderRadius: 2,
                border: "1px solid",
                borderColor:
                  selectedPermissions.length > 0 ? "primary.main" : "divider",
                minWidth: 400,
                maxWidth: 400,
                flexShrink: 0,
                transition: "border-color 0.2s, background-color 0.2s",
              }}
            >
              {/* Bulk Assign Bar — shown when rows are selected */}
              <Collapse in={selectedPermissions.length > 0} unmountOnExit>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <CheckBoxIcon
                      sx={{ fontSize: 18, color: "primary.main" }}
                    />
                    <Typography
                      variant='subtitle2'
                      fontWeight={600}
                      color='primary.main'
                    >
                      {selectedPermissions.length} selected
                    </Typography>
                  </Box>

                  <FormControl size='small' sx={{ flex: 1 }}>
                    <Select
                      value={bulkRole}
                      onChange={(e) => setBulkRole(e.target.value)}
                      disabled={selectedUserInactive}
                      displayEmpty
                      sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                    >
                      <MenuItem value='' disabled>
                        <em>Assign role...</em>
                      </MenuItem>
                      <MenuItem value='none'>No Access</MenuItem>
                      <MenuItem value='viewer'>View only</MenuItem>
                      <MenuItem value='admin'>Full Control</MenuItem>
                    </Select>
                  </FormControl>

                  <span>
                    <Tooltip
                      title={
                        !bulkRole
                          ? "Select a role first"
                          : bulkAssigning
                            ? "Applying..."
                            : "Apply to selected"
                      }
                      placement='top'
                    >
                      <Button
                        variant='contained'
                        onClick={handleBulkAssignRole}
                        disabled={
                          selectedUserInactive || !bulkRole || bulkAssigning
                        }
                        sx={{
                          height: 40,
                          minWidth: 80,
                          bgcolor: "primary.main",
                          "&:hover": { bgcolor: "primary.dark" },
                          "&:disabled": {
                            bgcolor: "action.disabledBackground",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        {bulkAssigning ? (
                          <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                          "Apply"
                        )}
                      </Button>
                    </Tooltip>

                    <Tooltip title='When off, restricted companies stay unchanged during bulk apply'>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          ml: 1,
                          flexBasis: "100%",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.75rem",
                            color: "text.secondary",
                          },
                        }}
                        control={
                          <Switch
                            size='small'
                            checked={includeRestrictedCompanies}
                            disabled={selectedUserInactive || bulkAssigning}
                            onChange={(e) =>
                              setIncludeRestrictedCompanies(e.target.checked)
                            }
                          />
                        }
                        label='Include restricted companies'
                      />
                    </Tooltip>
                  </span>
                </Box>
              </Collapse>

              {/* Replicate Permissions — shown when nothing is selected */}
              <Collapse in={selectedPermissions.length === 0} unmountOnExit>
                <Box
                  onClick={() => setCopyPermissionsExpanded((prev) => !prev)}
                  sx={{
                    mb: copyPermissionsExpanded ? 1.5 : 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 0.75,
                    "&:hover": { bgcolor: "action.hover" },
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    fontWeight={600}
                    color='text.primary'
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <CompareArrowsIcon
                      sx={{ fontSize: 18, color: "info.main" }}
                    />
                    Replicate Permissions
                  </Typography>
                  <IconButton
                    size='small'
                    sx={{ mt: -0.5, mr: -0.5 }}
                    aria-label={copyPermissionsExpanded ? "Collapse" : "Expand"}
                  >
                    {copyPermissionsExpanded ? (
                      <ExpandLessIcon fontSize='small' />
                    ) : (
                      <ExpandMoreIcon fontSize='small' />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={copyPermissionsExpanded}>
                  <Box
                    sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                  >
                    <FormControl fullWidth size='small'>
                      <Select
                        displayEmpty
                        value={importFromUserId}
                        onChange={(e) => setImportFromUserId(e.target.value)}
                        disabled={selectedUserInactive}
                        sx={{
                          bgcolor: "background.paper",
                          borderRadius: 1.5,
                          "& .MuiSelect-select": { py: 1.25 },
                        }}
                        renderValue={(value) => {
                          if (!value) {
                            return (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                Select user to copy from...
                              </Typography>
                            );
                          }
                          const user = filteredUsers.find(
                            (u) => u?.u_id === value
                          );
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AccountCircleIcon
                                sx={{ fontSize: 16, color: "action.active" }}
                              />
                              <Typography
                                variant='body2'
                                noWrap
                                sx={{ flex: 1 }}
                              >
                                {user?.name || user?.email || "Unknown User"}
                              </Typography>
                            </Box>
                          );
                        }}
                      >
                        <MenuItem value='' disabled>
                          <em>Select a source user</em>
                        </MenuItem>
                        {filteredUsers
                          .filter((u) => u?.u_id && u.u_id !== selectedUserId)
                          .map((u) => (
                            <MenuItem
                              key={u.u_id}
                              value={u.u_id}
                              sx={{ px: 2, py: 1 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  width: "100%",
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  fontWeight={500}
                                  noWrap
                                >
                                  {u.name || "Unnamed User"}
                                </Typography>
                                {u.email && (
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    noWrap
                                  >
                                    {u.email}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>

                    <Tooltip
                      title={
                        !importFromUserId
                          ? "Select a source user first"
                          : importingPermission
                            ? "Importing..."
                            : "Copy all permissions"
                      }
                      placement='top'
                    >
                      <span>
                        <Button
                          variant='contained'
                          onClick={handleImportPermissions}
                          disabled={
                            selectedUserInactive ||
                            !importFromUserId ||
                            importingPermission
                          }
                          sx={{
                            height: 40,
                            minWidth: 90,
                            bgcolor: "primary.main",
                            "&:hover": { bgcolor: "primary.dark" },
                            "&:disabled": {
                              bgcolor: "action.disabledBackground",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {importingPermission ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                          )}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  {importFromUserId && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        This will replace all current permissions for this user
                      </Typography>
                    </Box>
                  )}
                </Collapse>
              </Collapse>
            </Paper>
          </Box>

          {/* Content: 权限列表 (Scrollable Area) */}
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {selectedUserInactive && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                This user hasn't joined or activated their account yet. You'll
                be able to change their permissions once they are active.
              </Alert>
            )}
            {loadingPermission ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 8,
                }}
              >
                {/* 使用简单的CircularProgress，实际项目中可替换为Skeleton */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "3px solid",
                    borderColor: "grey.200",
                    borderTopColor: "primary.main",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              </Box>
            ) : userPermissions.length === 0 ? (
              <Alert severity='info' sx={{ mt: 2 }}>
                This user has no permissions assigned yet. Start by adding
                access to a company.
              </Alert>
            ) : (
              <Box sx={{ maxWidth: 1000, mx: "auto" }}>
                {/* Table Header (Visual Only) */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "48px 2fr 1fr 1.5fr 80px",
                    px: 2,
                    py: 1,
                    mb: 1,
                    typography: "caption",
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  <Box>
                    <Checkbox
                      size='small'
                      checked={isAllSelected}
                      indeterminate={isSomeSelected}
                      onChange={(e) =>
                        handleSelectAllPermissions(e.target.checked)
                      }
                      disabled={selectedUserInactive}
                      sx={{ p: 0 }}
                    />
                  </Box>
                  <Box>Company</Box>
                  <Box>Role</Box>
                  <Box>Scope (Years)</Box>
                  <Box sx={{ textAlign: "right" }}>Action</Box>
                </Box>

                <Stack spacing={1}>
                  {userPermissions.map((permission, index) => {
                    const isEditing = editingPermission === index;
                    const hasPartialAccess = permission.is_partial_access;
                    const availableYears = getAvailableYears(permission);
                    return (
                      <Box
                        key={permission.c_id || index}
                        sx={{
                          bgcolor: "background.paper",
                          border: 1,
                          borderColor: isEditing ? "primary.main" : "divider",
                          borderRadius: 2,
                          overflow: "hidden",
                          transition: "all 0.2s",
                          boxShadow: isEditing ? 4 : 0,
                          bgcolor: isEditing
                            ? "primary.lighter"
                            : "background.paper",
                        }}
                      >
                        {/* Row Content */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "48px 2fr 1fr 1.5fr 80px",
                            alignItems: "center",
                            p: 2,
                            gap: 2,
                          }}
                        >
                          {/* Checkbox */}
                          <Box>
                            <Checkbox
                              size='small'
                              checked={selectedPermissions.includes(index)}
                              onChange={() =>
                                handleToggleSelectPermission(index)
                              }
                              disabled={isEditing || selectedUserInactive}
                            />
                          </Box>
                          {/* Company Info */}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant='subtitle2'
                              fontWeight={600}
                              noWrap
                            >
                              {permission.company_name}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              noWrap
                            >
                              UEN: {permission.company_uen}
                            </Typography>
                            {permission.isRestricted && <RestrictedChip />}
                          </Box>

                          {/* Role Badge */}
                          <Box>
                            {!isEditing ? (
                              <RoleChip role={permission.role || "none"} />
                            ) : (
                              <RoleButtonGroup
                                value={permission.role || "none"}
                                onChange={(r) =>
                                  handleUpdatePermission(index, "role", r)
                                }
                                disabled={selectedUserInactive}
                                roles={["none", "viewer", "admin"]}
                              />
                            )}
                          </Box>

                          {/* Scope / Years */}
                          <Box>
                            {permission.role === "none" ? (
                              <Typography
                                variant='caption'
                                color='text.disabled'
                              >
                                -
                              </Typography>
                            ) : !isEditing ? (
                              <YearAccessEditor
                                readOnly
                                hasPartialAccess={hasPartialAccess}
                                allowed_yoa={permission.allowed_yoa || []}
                                availableYears={[]}
                                fullAccessLabel='All Years'
                                align='left'
                              />
                            ) : (
                              <YearAccessEditor
                                hasPartialAccess={hasPartialAccess}
                                onPartialChange={(value) =>
                                  handleUpdatePermission(
                                    index,
                                    "is_partial_access",
                                    value
                                  )
                                }
                                allowed_yoa={permission.allowed_yoa || []}
                                availableYears={availableYears}
                                onToggleYear={(year) =>
                                  handleToggleYear(index, year)
                                }
                                onSelectAllYears={(selectAll) =>
                                  handleSelectAllYears(index, selectAll)
                                }
                                disabled={selectedUserInactive}
                                fullAccessLabel='All Years'
                                align='left'
                              />
                            )}
                          </Box>

                          {/* Actions */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            {!isEditing ? (
                              <IconButton
                                size='small'
                                onClick={() => handleStartEdit(index)}
                                disabled={selectedUserInactive}
                                sx={{ color: "text.secondary" }}
                              >
                                <EditIcon fontSize='small' />
                              </IconButton>
                            ) : (
                              <Stack direction='row' spacing={0.5}>
                                <Tooltip title='Cancel'>
                                  <IconButton
                                    size='small'
                                    onClick={handleCancelEdit}
                                    color='error'
                                  >
                                    <CancelIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    savingPermission
                                      ? "Saving..."
                                      : "Save Changes"
                                  }
                                >
                                  <IconButton
                                    size='small'
                                    onClick={() =>
                                      handleSavePermission(permission)
                                    }
                                    disabled={
                                      selectedUserInactive || savingPermission
                                    }
                                    color='success'
                                  >
                                    {savingPermission ? (
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          border: "2px solid currentColor",
                                          borderTopColor: "transparent",
                                          borderRadius: "50%",
                                          animation: "spin 1s linear infinite",
                                        }}
                                      />
                                    ) : (
                                      <SaveIcon fontSize='small' />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default UserMode;
